"use client";

import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { formatBackgroundStyle } from "@/utils/styles/format";
import { useEffect, useRef, useState } from "react";

interface ParaText1Props {
  data?: {
    data?: {
      description?: string;
      text_color?: string;
      status?: boolean | number | string;
      backvariant?: "variant_1" | "variant_2" | "variant_3" | string;
      desktop_background_color?: string;
      mobile_background_color?: string;
      gradient_color_1?: string;
      gradient_color_2?: string;
      gradient_color_3?: string;
      gradient_color_4?: string;
      /** Legacy single background (fallback if desktop/mobile unset) */
      background_color?: string;
    };
  };
}

export default function ParaText1({ data }: ParaText1Props) {
  const sectionData = data?.data || (data as any);
  const isMobile = useIsMobile(1230);

  const ref = useRef<HTMLParagraphElement>(null);
  const [progress, setProgress] = useState(0);

  if (!sectionData) {
    return null;
  }

  const isActive =
    sectionData?.status === true ||
    sectionData?.status === 1 ||
    sectionData?.status === "1";

  if (!isActive) {
    return null;
  }

  const legacyBg = sectionData?.background_color;
  const desktopBg =
    sectionData?.desktop_background_color?.trim() || legacyBg || "#FFFFFF";
  const mobileBg =
    sectionData?.mobile_background_color?.trim() || legacyBg || "#FFFFFF";

  const bgStyle = formatBackgroundStyle(
    {
      backvariant:
        (sectionData?.backvariant as "variant_1" | "variant_2" | "variant_3") ||
        "variant_3",
      gradient_color_1: sectionData?.gradient_color_1,
      gradient_color_2: sectionData?.gradient_color_2,
      gradient_color_3: sectionData?.gradient_color_3,
      gradient_color_4: sectionData?.gradient_color_4,
      desktop_background_color: desktopBg,
      mobile_background_color: mobileBg,
    },
    Boolean(isMobile),
    desktopBg,
  );

  const isAnimationEnabled = sectionData?.enable_animation === true || sectionData?.enable_animation === 1 || sectionData?.enable_animation === "1";

  const description = sectionData?.description?.trim() || "";
  const words = description?.split(/\s+/);

  const animateWords = () =>
    words?.map((word: string, index: number) => {
      // Each word gets its own "start time"
      const visibility = progress * words.length - index;

      // clamp 0 → 1
      const clamped = Math.min(Math.max(visibility, 0.2), 1);

      // easing (makes it smooth, not robotic)
      const eased = 1 - Math.pow(1 - clamped, 3);

      // blur + opacity
      const blur = 4 * (1 - eased);
      const opacity = eased;

      return (
        <span
          key={word + index}
          style={{
            filter: `blur(${blur}px)`,
            opacity,
            transition: "filter 0.2s ease-out, opacity 0.2s ease-out",
            lineHeight: 1.5,
          }}
        >
          {word}
          {index < words.length - 1 ? " " : ""}
        </span>
      );
    });

  useEffect(() => {
    if (!isAnimationEnabled) return;
    const handleScroll = () => {
      if (!ref.current) return;

      const rect = ref.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      //Start & end control (tweak these if needed)
      const start = windowHeight * 0.8; // animation starts
      const end = windowHeight * 0.4; // animation ends earlier

      const scrolled = (start - rect.top) / (start - end);
      const clamped = Math.min(Math.max(scrolled, 0), 1);

      setProgress(clamped);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section
      className={cn(
        "flex w-full flex-col items-center lg:justify-center",
        /* mobile 4660:10109 — 20×40 (px-5 = 20px); sm: 3574 — 60×40; md: 3286 — 80×40; lg: 5063:11885 — 100×60; xl: 5063:11883 — 160×60 */
        "px-5 py-[40px] sm:px-[60px] sm:py-10 md:px-[80px] lg:px-[100px] lg:py-[60px] xl:px-[160px]",
      )}
      style={{
        ...bgStyle,
        color: sectionData?.text_color || "#333333",
      }}
    >
      <div className="mx-auto w-full max-w-[1080px] md:max-w-[1230px] lg:max-w-[1430px] xl:max-w-[1750px]">
        {description ? (
          <p
            ref={ref}
            className={cn(
              "m-0 w-full whitespace-pre-wrap text-center font-bold not-italic",
              /* Below xs: stack; xs: 18/1.2 (4660 / 3583); sm+ desktop scale */
              "text-lg leading-snug xs:text-[18px] xs:leading-120 sm:text-[26px] sm:leading-120 md:text-[36px] md:leading-120 lg:text-[40px] lg:leading-120",
            )}
            style={{ fontFamily: "var(--font-merriweather)" }}
          >
            {isAnimationEnabled ? animateWords() : description}
          </p>
        ) : null}
      </div>
    </section>
  );
}
